(() => {
  if (customElements.get("rao-gift-detail-modal")) return;

  class RAOGiftDetailModal extends HTMLElement {
    constructor() {
      super();

      this.productCache = new Map();
      this.images = [];
      this.currentImageIndex = 0;
      this.requestId = 0;
      this.touchStartX = null;
      this.closeTimer = null;

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onTriggerPrefetch = this.onTriggerPrefetch.bind(this);
      this.onModalClick = this.onModalClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onAdd = this.onAdd.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.updateMainImageClip = this.updateMainImageClip.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector(".rao-gift-modal__dialog");
      this.closeButton = this.querySelector(".rao-gift-modal__close");
      this.mainImage = this.querySelector("[data-gift-main-image]");
      this.dots = this.querySelector("[data-gift-gallery-dots]");
      this.thumbnails = this.querySelector("[data-gift-gallery-thumbnails]");
      this.stage = this.querySelector("[data-gift-gallery-stage]");
      this.productTitle = this.querySelector("[data-gift-product-title]");
      this.descriptionWrap = this.querySelector("[data-gift-description-wrap]");
      this.productDescription = this.querySelector("[data-gift-product-description]");
      this.descriptionToggle = this.querySelector("[data-gift-description-toggle]");
      this.descriptionToggleText = this.querySelector("[data-gift-description-toggle-text]");
      this.productPrice = this.querySelector("[data-gift-product-price]");
      this.addButton = this.querySelector("[data-gift-add]");
      this.addButtonText = this.querySelector("[data-gift-add-text]");
      this.error = this.querySelector("[data-gift-error]");
      this.baseAddLabel = this.dataset.addLabel || "Toevoegen";
      this.section = this.closest(".addon-section--section");
      this.triggers = Array.from(this.section?.querySelectorAll(".rao-gift-detail-trigger") || []);

      this.triggers.forEach((trigger) => {
        trigger.addEventListener("click", this.onTriggerClick);
        trigger.addEventListener("pointerenter", this.onTriggerPrefetch, { passive: true });
        trigger.addEventListener("focus", this.onTriggerPrefetch, { passive: true });
      });

      this.addEventListener("click", this.onModalClick);
      this.addButton?.addEventListener("click", this.onAdd);
      this.stage?.addEventListener("touchstart", this.onTouchStart, { passive: true });
      this.stage?.addEventListener("touchend", this.onTouchEnd, { passive: true });
      this.mainImage?.addEventListener("load", this.updateMainImageClip);

      if ("ResizeObserver" in window && this.stage) {
        this.galleryResizeObserver = new ResizeObserver(() => {
          if (!this.hidden) this.updateMainImageClip();
        });
        this.galleryResizeObserver.observe(this.stage);
        if (this.mainImage) this.galleryResizeObserver.observe(this.mainImage);
      }

      if ("ResizeObserver" in window && this.productDescription) {
        this.descriptionResizeObserver = new ResizeObserver(() => {
          if (!this.hidden) this.updateDescriptionDisclosure();
        });
        this.descriptionResizeObserver.observe(this.productDescription);
      }
    }

    disconnectedCallback() {
      this.triggers?.forEach((trigger) => {
        trigger.removeEventListener("click", this.onTriggerClick);
        trigger.removeEventListener("pointerenter", this.onTriggerPrefetch);
        trigger.removeEventListener("focus", this.onTriggerPrefetch);
      });

      this.removeEventListener("click", this.onModalClick);
      this.addButton?.removeEventListener("click", this.onAdd);
      this.stage?.removeEventListener("touchstart", this.onTouchStart);
      this.stage?.removeEventListener("touchend", this.onTouchEnd);
      this.mainImage?.removeEventListener("load", this.updateMainImageClip);
      this.descriptionResizeObserver?.disconnect();
      this.galleryResizeObserver?.disconnect();
      document.removeEventListener("keydown", this.onKeyDown);
      clearTimeout(this.closeTimer);
      document.documentElement.classList.remove("rao-gift-modal-open");
    }

    onTriggerClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.open(event.currentTarget);
    }

    onTriggerPrefetch(event) {
      const handle = event.currentTarget?.dataset.productHandle;
      if (handle) this.loadProduct(handle).catch(() => {});
    }

    onModalClick(event) {
      if (event.target.closest("[data-gift-modal-close]")) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.target.closest("[data-gift-gallery-prev]")) {
        event.preventDefault();
        this.selectImage(this.currentImageIndex - 1);
        return;
      }

      if (event.target.closest("[data-gift-gallery-next]")) {
        event.preventDefault();
        this.selectImage(this.currentImageIndex + 1);
        return;
      }

      if (event.target.closest("[data-gift-description-toggle]")) {
        event.preventDefault();
        this.toggleDescription();
      }
    }

    async open(trigger) {
      clearTimeout(this.closeTimer);
      this.classList.remove("is-closing");
      this.trigger = trigger;
      this.card = trigger.closest(".js-wizard-product-card");
      this.handle = trigger.dataset.productHandle || "";
      this.variantId = String(trigger.dataset.variantId || this.card?.querySelector('input[name="id"]')?.value || "");
      this.requestId += 1;
      const activeRequest = this.requestId;

      const fallbackTitle = trigger.dataset.productTitle || "Cadeautje";
      const fallbackImage = this.normalizeImageUrl(trigger.dataset.featuredImage || this.card?.querySelector("img")?.src || "");
      const titleParts = this.splitMarketingTitle(fallbackTitle);

      this.productTitle.textContent = titleParts.title;
      this.renderDescription("", titleParts.description);
      this.productPrice.textContent = trigger.dataset.productPrice || this.card?.querySelector(".addon-section__tab--product-price")?.textContent?.trim() || "";
      this.renderGallery(fallbackImage ? [fallbackImage] : [], titleParts.title);
      this.resetAddButton();
      this.hideError();
      this.setLoading(true);

      this.hidden = false;
      this.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("rao-gift-modal-open");
      document.addEventListener("keydown", this.onKeyDown);
      this.dialog.scrollTop = 0;

      requestAnimationFrame(this.updateMainImageClip);
      requestAnimationFrame(() => this.dialog?.focus({ preventScroll: true }));

      try {
        const product = await this.loadProduct(this.handle);
        if (activeRequest !== this.requestId || this.hidden) return;

        const productTitle = fallbackTitle || product?.title || "Cadeautje";
        const productTitleParts = this.splitMarketingTitle(productTitle);
        const images = this.getProductImages(product, fallbackImage, this.variantId);

        this.productTitle.textContent = productTitleParts.title;
        this.renderDescription(product?.description || "", productTitleParts.description);
        this.renderGallery(images, productTitleParts.title);
      } catch (error) {
        console.warn("Cadeau-informatie kon niet volledig worden geladen.", error);
      } finally {
        if (activeRequest === this.requestId) this.setLoading(false);
      }
    }

    close({ restoreFocus = true, immediate = false } = {}) {
      if (this.hidden) return Promise.resolve();

      this.requestId += 1;
      document.removeEventListener("keydown", this.onKeyDown);

      const finish = () => {
        document.documentElement.classList.remove("rao-gift-modal-open");
        this.hidden = true;
        this.setAttribute("aria-hidden", "true");
        this.classList.remove("is-closing", "is-loading");
        this.addButton.disabled = false;
        this.resetAddButton();
        if (restoreFocus) this.trigger?.focus({ preventScroll: true });
      };

      if (immediate || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        finish();
        return Promise.resolve();
      }

      this.classList.add("is-closing");
      return new Promise((resolve) => {
        this.closeTimer = window.setTimeout(() => {
          finish();
          resolve();
        }, 180);
      });
    }

    async onAdd(event) {
      event.preventDefault();
      event.stopPropagation();

      if (!this.card || this.addButton.disabled) return;

      const cardToSelect = this.card;
      const giftTab = cardToSelect.closest(".addon-section--tab");
      const wizard = this.section?.querySelector("addon-section");
      const shouldAdvanceToDelivery = giftTab?.dataset.index === "2" && giftTab.classList.contains("is-active-step");
      this.addButton.disabled = true;
      this.addButtonText.textContent = "Toevoegen...";
      this.hideError();

      await this.close({ restoreFocus: false });

      requestAnimationFrame(() => {
        cardToSelect.click();
      });

      window.setTimeout(() => {
        const activeTab = this.section?.querySelector(".addon-section--tab.is-active-step");

        // De bestaande wizard gaat na 400 ms zelf door. Dit is alleen een
        // vangnet voor stap 3, zodat een geselecteerd cadeau altijd bij stap 4 uitkomt.
        if (
          shouldAdvanceToDelivery &&
          cardToSelect.classList.contains("selected") &&
          activeTab?.dataset.index === "2" &&
          typeof wizard?.goToStep === "function"
        ) {
          wizard.goToStep(3, false);
        }

        const activeStep = this.section?.querySelector(".addon-section--tab.is-active-step");
        const heading = activeStep?.querySelector("h3, .rao-wizard-cards-title, .addon-section__tab--date-title");
        if (heading) {
          heading.setAttribute("tabindex", "-1");
          heading.focus({ preventScroll: true });
        }
      }, 650);
    }

    onKeyDown(event) {
      if (this.hidden) return;

      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.selectImage(this.currentImageIndex - 1);
        return;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.selectImage(this.currentImageIndex + 1);
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = Array.from(
        this.dialog.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => element.offsetParent !== null);

      if (!focusable.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onTouchStart(event) {
      this.touchStartX = event.changedTouches?.[0]?.clientX ?? null;
    }

    onTouchEnd(event) {
      if (this.touchStartX === null || this.images.length < 2) return;
      const endX = event.changedTouches?.[0]?.clientX;
      if (typeof endX !== "number") return;

      const delta = endX - this.touchStartX;
      this.touchStartX = null;

      if (Math.abs(delta) < 45) return;
      this.selectImage(delta > 0 ? this.currentImageIndex - 1 : this.currentImageIndex + 1);
    }

    updateMainImageClip() {
      const image = this.mainImage;
      if (!image?.naturalWidth || !image?.naturalHeight) return;

      const boxWidth = image.clientWidth;
      const boxHeight = image.clientHeight;
      if (!boxWidth || !boxHeight) return;

      const imageRatio = image.naturalWidth / image.naturalHeight;
      const boxRatio = boxWidth / boxHeight;
      let top = 0;
      let right = 0;
      let bottom = 0;
      let left = 0;

      if (imageRatio > boxRatio) {
        const visibleHeight = boxWidth / imageRatio;
        top = bottom = Math.max(0, (boxHeight - visibleHeight) / 2);
      } else {
        const visibleWidth = boxHeight * imageRatio;
        left = right = Math.max(0, (boxWidth - visibleWidth) / 2);
      }

      const clip = `inset(${top}px ${right}px ${bottom}px ${left}px round 10px)`;
      image.style.clipPath = clip;
      image.style.webkitClipPath = clip;
    }

    loadProduct(handle) {
      if (!handle) return Promise.reject(new Error("Producthandle ontbreekt"));
      if (this.productCache.has(handle)) return this.productCache.get(handle);

      const root = window.Shopify?.routes?.root || "/";
      const url = `${root}products/${encodeURIComponent(handle)}.js`;
      const request = fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      }).then((response) => {
        if (!response.ok) throw new Error(`Product laden mislukt (${response.status})`);
        return response.json();
      });

      this.productCache.set(handle, request);
      request.catch(() => this.productCache.delete(handle));
      return request;
    }

    splitMarketingTitle(value) {
      const title = String(value || "Cadeautje").replace(/\s+/g, " ").trim();
      const match = title.match(/^(.{1,105}?[.!?])\s+(.+)$/);

      if (match) {
        return { title: match[1].trim(), description: match[2].trim() };
      }

      return { title, description: "Een leuk extraatje om je verrassing compleet te maken." };
    }

    renderDescription(html, fallbackText) {
      const safeHtml = this.sanitizeDescription(html);
      if (safeHtml) {
        const probe = document.createElement("div");
        probe.innerHTML = safeHtml;
        const descriptionText = probe.textContent.replace(/\s+/g, " ").trim();
        const visibleTitle = this.productTitle.textContent.replace(/\s+/g, " ").trim();

        if (visibleTitle && descriptionText.toLocaleLowerCase().startsWith(visibleTitle.toLocaleLowerCase())) {
          const remainder = descriptionText.slice(visibleTitle.length).trim();
          this.productDescription.textContent = remainder || fallbackText || "Een leuk extraatje om je verrassing compleet te maken.";
        } else {
          this.productDescription.innerHTML = safeHtml;
        }
      } else {
        this.productDescription.textContent = fallbackText || "Een leuk extraatje om je verrassing compleet te maken.";
      }

      this.resetDescriptionDisclosure();
      requestAnimationFrame(() => this.updateDescriptionDisclosure());
    }

    resetDescriptionDisclosure() {
      if (!this.descriptionWrap || !this.descriptionToggle) return;

      this.descriptionWrap.classList.remove("is-collapsible", "is-expanded");
      this.descriptionWrap.style.removeProperty("--gift-description-collapsed-height");
      this.descriptionToggle.hidden = true;
      this.descriptionToggle.setAttribute("aria-expanded", "false");
      if (this.descriptionToggleText) this.descriptionToggleText.textContent = "Lees meer";
    }

    updateDescriptionDisclosure() {
      if (!this.descriptionWrap || !this.productDescription || !this.descriptionToggle || this.hidden) return;

      const descriptionText = this.productDescription.textContent.replace(/\s+/g, " ").trim();
      const lineHeight = Number.parseFloat(window.getComputedStyle(this.productDescription).lineHeight) || 21;
      const collapsedHeight = Math.ceil(lineHeight * 4);
      const isLong = descriptionText.length > 0 && this.productDescription.scrollHeight > collapsedHeight + 3;

      this.descriptionWrap.style.setProperty("--gift-description-collapsed-height", `${collapsedHeight}px`);
      this.descriptionWrap.classList.toggle("is-collapsible", isLong);
      this.descriptionToggle.hidden = !isLong;
    }

    toggleDescription() {
      if (!this.descriptionWrap?.classList.contains("is-collapsible")) return;

      const isExpanded = this.descriptionWrap.classList.toggle("is-expanded");
      this.descriptionToggle.setAttribute("aria-expanded", String(isExpanded));
      if (this.descriptionToggleText) {
        this.descriptionToggleText.textContent = isExpanded ? "Minder tonen" : "Lees meer";
      }
    }

    sanitizeDescription(html) {
      if (!html) return "";

      const template = document.createElement("template");
      template.innerHTML = String(html);
      template.content.querySelectorAll("script, style, iframe, object, embed, form, input, button").forEach((element) => element.remove());

      template.content.querySelectorAll("*").forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
          const name = attribute.name.toLowerCase();
          const value = attribute.value.trim().toLowerCase();
          if (name.startsWith("on") || ((name === "href" || name === "src") && value.startsWith("javascript:"))) {
            element.removeAttribute(attribute.name);
          }
        });
      });

      return template.innerHTML.trim();
    }

    getProductImages(product, fallbackImage, variantId) {
      const candidates = [];
      const candidateKeys = new Set();
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      const selectedVariant = variants.find((variant) => String(variant.id) === String(variantId));

      const addImage = (image) => {
        const raw = typeof image === "string" ? image : image?.src || image?.url;
        const normalized = this.normalizeImageUrl(raw);
        const key = this.getImageKey(normalized);
        if (normalized && key && !candidateKeys.has(key)) {
          candidateKeys.add(key);
          candidates.push(normalized);
        }
      };

      addImage(selectedVariant?.featured_image);
      addImage(product?.featured_image);
      (product?.images || []).forEach(addImage);
      addImage(fallbackImage);

      return candidates;
    }

    normalizeImageUrl(value) {
      if (!value) return "";
      const url = String(value);
      return url.startsWith("//") ? `https:${url}` : url;
    }

    getImageKey(value) {
      if (!value) return "";

      try {
        const url = new URL(value, window.location.origin);
        return (url.pathname.split("/").pop() || url.pathname).toLocaleLowerCase();
      } catch (_) {
        const path = String(value).split("?")[0];
        return (path.split("/").pop() || path).toLocaleLowerCase();
      }
    }

    renderGallery(images, productTitle) {
      this.images = Array.from(new Set((images || []).filter(Boolean)));
      this.currentImageIndex = 0;
      this.dots.innerHTML = "";
      this.thumbnails.innerHTML = "";
      this.classList.toggle("has-single-image", this.images.length <= 1);

      if (!this.images.length) {
        this.mainImage.removeAttribute("src");
        this.mainImage.alt = "";
        return;
      }

      this.images.forEach((image, index) => {
        const dot = document.createElement("span");
        dot.className = "rao-gift-modal__dot";
        this.dots.appendChild(dot);

        const thumbnail = document.createElement("button");
        thumbnail.type = "button";
        thumbnail.className = "rao-gift-modal__thumbnail";
        thumbnail.setAttribute("aria-label", `Bekijk afbeelding ${index + 1} van ${this.images.length}`);
        thumbnail.addEventListener("click", () => this.selectImage(index));

        const thumbnailImage = document.createElement("img");
        thumbnailImage.src = image;
        thumbnailImage.alt = "";
        thumbnailImage.loading = index === 0 ? "eager" : "lazy";
        thumbnail.appendChild(thumbnailImage);
        this.thumbnails.appendChild(thumbnail);
      });

      this.galleryTitle = productTitle || "Cadeautje";
      this.selectImage(0, false);
    }

    selectImage(index, scrollThumbnail = true) {
      if (!this.images.length) return;

      const total = this.images.length;
      this.currentImageIndex = (index + total) % total;
      this.mainImage.src = this.images[this.currentImageIndex];
      this.mainImage.alt = `${this.galleryTitle}, afbeelding ${this.currentImageIndex + 1} van ${total}`;
      if (this.mainImage.complete) requestAnimationFrame(this.updateMainImageClip);

      Array.from(this.dots.children).forEach((dot, dotIndex) => {
        dot.classList.toggle("is-active", dotIndex === this.currentImageIndex);
      });

      Array.from(this.thumbnails.children).forEach((thumbnail, thumbnailIndex) => {
        const isActive = thumbnailIndex === this.currentImageIndex;
        thumbnail.classList.toggle("is-active", isActive);
        thumbnail.setAttribute("aria-current", isActive ? "true" : "false");
        if (isActive && scrollThumbnail) {
          thumbnail.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        }
      });
    }

    setLoading(isLoading) {
      this.classList.toggle("is-loading", isLoading);
      this.dialog?.setAttribute("aria-busy", isLoading ? "true" : "false");
    }

    resetAddButton() {
      if (this.addButton) this.addButton.disabled = false;
      if (this.addButtonText) this.addButtonText.textContent = this.baseAddLabel;
    }

    hideError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = "";
    }
  }

  customElements.define("rao-gift-detail-modal", RAOGiftDetailModal);
})();
